import React, { useEffect, useState, useRef } from 'react'
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Switch,
} from '@mui/material'
import HandshakeIcon from '@mui/icons-material/Handshake'
import BuildIcon from '@mui/icons-material/Build'
import BlockIcon from '@mui/icons-material/Block'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckIcon from '@mui/icons-material/Check'
import WarningIcon from '@mui/icons-material/Warning'
import SyncIcon from '@mui/icons-material/Sync'
import ClearIcon from '@mui/icons-material/Clear'
import RefreshIcon from '@mui/icons-material/Refresh'
import ReplayIcon from '@mui/icons-material/Replay'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'

import FilterListIcon from '@mui/icons-material/FilterList'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import DownloadIcon from '@mui/icons-material/Download'
import Autocomplete from '@mui/material/Autocomplete'
import Menu from '@mui/material/Menu'
import EditIcon from '@mui/icons-material/Edit'

import { useParams, useNavigate } from 'react-router-dom'
import { useNotify, useTranslate, useCreate } from 'react-admin'
import { LoadingButton } from '@mui/lab'
import dayjs from 'dayjs'
import { CardCommon } from 'src/components/uiElements'
import { hasPermissionFunction } from 'src/helper/HasPermission'
import { supplierConciliationServices } from 'src/services/purchases/supplier_conciliation/supplierConciliation.service'
import { itemSupplierServices } from 'src/services/purchases/item_supplier/ItemSupplier.service'
import { supplierPaymentOrderServices } from 'src/services/purchases/supplierPaymentOrder/SupplierPaymentOrder.service'
import { iconAccordingProduct } from 'src/components/pages/sales/reservation/helper/IconAccordingProduct'
import { getConciliationTypeInfo } from './conciliationTypeHelper'
import { supplierBillingServices } from 'src/services/purchases/supplier_billing/SupplierBilling.service'
import { basesServices } from 'src/services/reservation/bases/Bases.service'
import { workUnitServices } from 'src/services/reservation/workUnit/WorkUnit.service'
import { clientServices } from 'src/services/auth/client/client.service'
import { GenerateInvoiceModal } from './GenerateInvoiceModal'
import { CheckItemsModal } from 'src/components/pages/purchases/supplier-billing/logic/CheckItemsModal'
import { ChangeConciliationStatusDialog } from './ChangeConciliationStatusDialog'

const STATUS_FILTER_OPTIONS = [
  { value: 'RECONCILED', labelKey: 'ra.field.reconciled_legend', defaultLabel: 'Conciliado' },
  { value: 'RECONCILED_ADJUSTED', labelKey: 'ra.field.reconciled_adjusted_legend', defaultLabel: 'Conciliado con Ajuste' },
  { value: 'RECONCILED_MANUALLY', labelKey: 'ra.field.reconciled_manually_legend', defaultLabel: 'Conciliado Manual' },
  { value: 'RECONCILED_DUPLICATE', labelKey: 'ra.field.reconciled_duplicate_legend', defaultLabel: 'Conciliado Duplicado' },
  { value: 'AMOUNT_MISMATCH', labelKey: 'ra.field.difference_legend', defaultLabel: 'Con Diferencia' },
  { value: 'NOT_FOUND', labelKey: 'ra.field.not_found_legend', defaultLabel: 'No Encontrado' },
  { value: 'UNRECONCILED', labelKey: 'ra.field.unreconciled_legend', defaultLabel: 'No Conciliado' },
]

const RemoveFilterField = ({ label, onRemove, children }) => (
  <Box width='100%'>
    <Typography variant='caption' sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#555', mb: 0.5, display: 'block' }}>
      {label}
    </Typography>
    <Box display='flex' alignItems='center' gap={0.5}>
      <IconButton size='small' onClick={onRemove} sx={{ padding: '2px' }}>
        <HighlightOffIcon fontSize='small' color='action' />
      </IconButton>
      <Box flex={1}>
        {children}
      </Box>
    </Box>
  </Box>
)


export const SupplierConciliationDetailView = ({ isReadOnly = false }) => {
  const { id } = useParams()
  const navigate = useNavigate()
  const translate = useTranslate()
  const notify = useNotify()
  const [create] = useCreate()

  const [loading, setLoading] = useState(true)
  const [itemsLoading, setItemsLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [conciliation, setConciliation] = useState(null)
  const [client, setClient] = useState(null)
  const [clientLoading, setClientLoading] = useState(false)
  const [items, setItems] = useState([])
  const [selectedFilter, setSelectedFilter] = useState('ALL') // ALL, RECONCILED, NOT_FOUND, AMOUNT_MISMATCH
  const [actionLoading, setActionLoading] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  // Selection checkboxes
  const [selectedItemIds, setSelectedItemIds] = useState([])

  const [filterState, setFilterState] = useState({
    code: '',
    status: [],
    is_billed: '',
    is_paid: '',
    reservation_number: '',
    bases: '',
    work_unit_id: '',
    min_difference_value: '',
    max_difference_value: '',
  })
  const [displayedFilters, setDisplayedFilters] = useState({})
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null)
  const [basesList, setBasesList] = useState([])

  // Fetch bases list when filter is active
  useEffect(() => {
    if (displayedFilters.bases && basesList.length === 0) {
      const loadBases = async () => {
        try {
          const data = await basesServices.getByAll({})
          setBasesList(Array.isArray(data) ? data : [])
        } catch (err) {
          console.error('Error fetching bases:', err)
        }
      }
      loadBases()
    }
  }, [displayedFilters.bases, basesList.length])

  const [selectedWorkUnitObj, setSelectedWorkUnitObj] = useState(null)
  const [workUnitsList, setWorkUnitsList] = useState([])
  const [loadingWorkUnits, setLoadingWorkUnits] = useState(false)
  const searchTimeoutRef = useRef(null)

  // Fetch work units list
  const loadWorkUnits = async (search = '') => {
    try {
      setLoadingWorkUnits(true)
      const params = search ? { search, long_name: search } : {}
      const data = await workUnitServices.getByAll(params)
      setWorkUnitsList(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Error fetching work units:', err)
    } finally {
      setLoadingWorkUnits(false)
    }
  }

  useEffect(() => {
    if (displayedFilters.work_unit_id && workUnitsList.length === 0) {
      loadWorkUnits()
    }
  }, [displayedFilters.work_unit_id, workUnitsList.length])

  // Generic confirmation modal state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  })

  // After-sales adjustment modal state
  const [afterSalesModal, setAfterSalesModal] = useState({
    open: false,
    item: null,
    destination: 'SAME_RESERVATION', // SAME_RESERVATION or NEW_RESERVATION
    adjustmentType: 'COST_ONLY', // COST_ONLY or COST_AND_SALE
  })

  // Retry conciliation modal state
  const [retryModal, setRetryModal] = useState({
    open: false,
    targetIds: [],
  })

  // OP Modal state
  const [opModal, setOpModal] = useState({
    open: false,
    observations: '',
    dueDate: '',
    submitting: false,
  })

  // Generate Invoice Modal state
  const [invoiceModal, setInvoiceModal] = useState({
    open: false,
    data: null,
    submitting: false,
  })

  // Check Duplicate Invoice Modal state
  const [checkInvoiceModal, setCheckInvoiceModal] = useState({
    open: false,
    itemsToCheck: [],
    formData: null,
    resolvedItems: [],
    submitting: false,
  })

  // Change Conciliation Status Dialog state
  const [changeStatusDialogOpen, setChangeStatusDialogOpen] = useState(false)

  useEffect(() => {
    if (id) {
      loadData()
    }
  }, [id])

  const loadData = async () => {
    try {
      setLoading(true)
      const data = await supplierConciliationServices.getConciliationById(Number(id))
      setConciliation(data)

      const hashClient = data?.client_relation?.hash_client || data?.hash_client
      if (hashClient) {
        setClientLoading(true)
        try {
          const clientData = await clientServices.getByHashClient(hashClient)
          setClient(clientData)
        } catch (clientErr) {
          console.error('Error fetching client by hash_client:', clientErr)
          setClient(null)
        } finally {
          setClientLoading(false)
        }
      } else {
        setClient(null)
      }

      await fetchItemsByFilter(selectedFilter, page, rowsPerPage, filterState)
    } catch (error) {
      console.error(error)
      notify(translate('ra.notification.http_error'), { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  // Fetch Items from Backend API according to active filter, page and limit
  const fetchItemsByFilter = async (
    filterKey = selectedFilter,
    pageNum = page,
    pageSize = rowsPerPage,
    currentFilterState = filterState
  ) => {
    setItemsLoading(true)
    try {
      const params = {
        skip: pageNum * pageSize,
        limit: pageSize,
        ordering: 'abs_difference_value.DESC',
      }

      if (currentFilterState.status && currentFilterState.status.length > 0) {
        params.status = currentFilterState.status.join(',')
      } else if (filterKey === 'RECONCILED_ALL') {
        params.status = 'RECONCILED,RECONCILED_ADJUSTED,RECONCILED_MANUALLY,RECONCILED_DUPLICATE'
      } else if (filterKey && filterKey !== 'ALL') {
        params.status = filterKey
      }

      if (currentFilterState.code && currentFilterState.code.trim()) {
        params.code = currentFilterState.code.trim()
      }

      if (currentFilterState.is_billed !== '' && currentFilterState.is_billed !== undefined && currentFilterState.is_billed !== null) {
        params.is_billed = currentFilterState.is_billed === 'true' || currentFilterState.is_billed === true
      }

      if (currentFilterState.is_paid !== '' && currentFilterState.is_paid !== undefined && currentFilterState.is_paid !== null) {
        params.is_paid = currentFilterState.is_paid === 'true' || currentFilterState.is_paid === true
      }

      if (currentFilterState.reservation_number && currentFilterState.reservation_number.trim()) {
        params.reservation_number = currentFilterState.reservation_number.trim()
      }

      if (currentFilterState.bases !== '' && currentFilterState.bases !== undefined && currentFilterState.bases !== null) {
        params.bases = currentFilterState.bases
      }

      if (currentFilterState.work_unit_id !== '' && currentFilterState.work_unit_id !== undefined && currentFilterState.work_unit_id !== null) {
        params.work_unit_id = currentFilterState.work_unit_id
      }

      if (currentFilterState.min_difference_value !== '' && currentFilterState.min_difference_value !== undefined && currentFilterState.min_difference_value !== null && !isNaN(currentFilterState.min_difference_value)) {
        params.min_difference_value = Number(currentFilterState.min_difference_value)
      }

      if (currentFilterState.max_difference_value !== '' && currentFilterState.max_difference_value !== undefined && currentFilterState.max_difference_value !== null && !isNaN(currentFilterState.max_difference_value)) {
        params.max_difference_value = Number(currentFilterState.max_difference_value)
      }

      const res = await supplierConciliationServices.getConciliationItems(Number(id), params)
      const itemsData = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : [])
      setItems(itemsData)
    } catch (error) {
      console.error(error)
      notify(translate('ra.notification.http_error'), { type: 'error' })
    } finally {
      setItemsLoading(false)
    }
  }

  // Filter Bar Handlers
  const handleApplyFilters = () => {
    setPage(0)
    fetchItemsByFilter(selectedFilter, 0, rowsPerPage, filterState)
  }

  const handleClearAllFilters = () => {
    const initial = {
      code: '',
      status: [],
      is_billed: '',
      is_paid: '',
      reservation_number: '',
      bases: '',
      work_unit_id: '',
      min_difference_value: '',
      max_difference_value: '',
    }
    setFilterState(initial)
    setSelectedWorkUnitObj(null)
    setDisplayedFilters({})
    setPage(0)
    fetchItemsByFilter(selectedFilter, 0, rowsPerPage, initial)
  }

  const handleRemoveFilter = (filterKey) => {
    setDisplayedFilters((prev) => {
      const copy = { ...prev }
      delete copy[filterKey]
      return copy
    })

    if (filterKey === 'work_unit_id') {
      setSelectedWorkUnitObj(null)
    }

    setFilterState((prev) => {
      const copy = { ...prev }
      if (filterKey === 'difference') {
        copy.min_difference_value = ''
        copy.max_difference_value = ''
      } else if (filterKey === 'status') {
        copy.status = []
      } else {
        copy[filterKey] = ''
      }
      return copy
    })
  }

  // Handle Stat Box Click (Re-queries API with selected filter)
  const handleFilterClick = async (filterKey) => {
    const initial = {
      code: '',
      status: [],
      is_billed: '',
      is_paid: '',
      reservation_number: '',
      bases: '',
      work_unit_id: '',
      min_difference_value: '',
      max_difference_value: '',
    }
    setFilterState(initial)
    setSelectedWorkUnitObj(null)
    setDisplayedFilters({})
    setSelectedFilter(filterKey)
    setSelectedItemIds([])
    setPage(0)
    await fetchItemsByFilter(filterKey, 0, rowsPerPage, initial)
  }

  // Checkbox Selection Logic
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = paginatedItems.map((n) => n.id)
      setSelectedItemIds(Array.from(new Set([...selectedItemIds, ...newSelecteds])))
    } else {
      const paginatedIds = new Set(paginatedItems.map((n) => n.id))
      setSelectedItemIds(selectedItemIds.filter((id) => !paginatedIds.has(id)))
    }
  }

  const handleItemSelectClick = (itemId) => {
    const selectedIndex = selectedItemIds.indexOf(itemId)
    let newSelected = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedItemIds, itemId)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedItemIds.slice(1))
    } else if (selectedIndex === selectedItemIds.length - 1) {
      newSelected = newSelected.concat(selectedItemIds.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedItemIds.slice(0, selectedIndex),
        selectedItemIds.slice(selectedIndex + 1)
      )
    }

    setSelectedItemIds(newSelected)
  }

  // Handle Forzar Conciliación (Status -> RECONCILED_MANUALLY)
  const handleForceReconcile = async (itemId) => {
    try {
      setActionLoading(itemId)
      if (supplierConciliationServices.updateItem) {
        await supplierConciliationServices.updateItem(itemId, { status: 'RECONCILED_MANUALLY' })
      } else {
        await supplierConciliationServices.reconcileItem(itemId)
      }
      notify(translate('ra.notification.updated_success_masculine', { element: translate('ra.field.status') }) || 'Ítem conciliado', { type: 'info' })
      await loadData()
    } catch (error) {
      notify(translate('ra.notification.http_error'), { type: 'error' })
    } finally {
      setActionLoading(null)
    }
  }

  // Handle Reprocesar item (POST /supplier-conciliation/item/{itemId}/reconcile)
  const handleReprocessItem = async (itemId) => {
    try {
      setActionLoading(itemId)
      await supplierConciliationServices.reconcileItem(itemId)
      notify(translate('ra.notification.updated_success_masculine', { element: translate('ra.field.status') }) || 'Ítem reprocesado', { type: 'info' })
      await loadData()
    } catch (error) {
      notify(translate('ra.notification.http_error'), { type: 'error' })
    } finally {
      setActionLoading(null)
    }
  }

  // Handle Export
  const handleExport = async () => {
    try {
      setExportLoading(true)
      const params = {}

      if (filterState.status && filterState.status.length > 0) {
        params.status = filterState.status.join(',')
      } else if (selectedFilter === 'RECONCILED_ALL') {
        params.status = 'RECONCILED,RECONCILED_ADJUSTED,RECONCILED_MANUALLY,RECONCILED_DUPLICATE'
      } else if (selectedFilter && selectedFilter !== 'ALL') {
        params.status = selectedFilter
      }

      if (filterState.code && filterState.code.trim()) {
        params.code = filterState.code.trim()
      }

      if (filterState.is_billed !== '' && filterState.is_billed !== undefined && filterState.is_billed !== null) {
        params.is_billed = filterState.is_billed === 'true' || filterState.is_billed === true
      }

      if (filterState.is_paid !== '' && filterState.is_paid !== undefined && filterState.is_paid !== null) {
        params.is_paid = filterState.is_paid === 'true' || filterState.is_paid === true
      }

      if (filterState.reservation_number && filterState.reservation_number.trim()) {
        params.reservation_number = filterState.reservation_number.trim()
      }

      if (filterState.bases !== '' && filterState.bases !== undefined && filterState.bases !== null) {
        params.bases = filterState.bases
      }

      if (filterState.work_unit_id !== '' && filterState.work_unit_id !== undefined && filterState.work_unit_id !== null) {
        params.work_unit_id = filterState.work_unit_id
      }

      if (filterState.min_difference_value !== '' && filterState.min_difference_value !== undefined && filterState.min_difference_value !== null && !isNaN(filterState.min_difference_value)) {
        params.min_difference_value = Number(filterState.min_difference_value)
      }

      if (filterState.max_difference_value !== '' && filterState.max_difference_value !== undefined && filterState.max_difference_value !== null && !isNaN(filterState.max_difference_value)) {
        params.max_difference_value = Number(filterState.max_difference_value)
      }

      await supplierConciliationServices.exportItemsDetail(Number(id), params)
      notify(translate('ra.notification.export_conciliation_items_scheduled'), { type: 'info' })

      // Mantener el botón deshabilitado 10 segundos adicionales para evitar peticiones duplicadas
      setTimeout(() => setExportLoading(false), 10000)
    } catch (error) {
      console.error(error)
      notify(translate('ra.notification.http_error'), { type: 'error' })
      setExportLoading(false)
    }
  }

  // Open After Sales Modal
  const handleOpenAfterSales = (item) => {
    setAfterSalesModal({
      open: true,
      item,
      destination: 'SAME_RESERVATION',
      adjustmentType: 'COST_ONLY',
    })
  }

  // Submit After Sales Action
  const handleAfterSalesSubmit = async () => {
    if (!afterSalesModal.item) return
    const item = afterSalesModal.item

    try {
      setActionLoading(item.id)

      // 1. Consultar /supplier-conciliation/item/{item_id}/associated-items para obtener los hash_items asociados
      const associatedItems = await supplierConciliationServices.getAssociatedItems(item.id)

      // 2. Filtrar los ítems que NO tengan status=AFTER_SALES ni DELETED y agarrar el primer hash_item
      const validItems = Array.isArray(associatedItems)
        ? associatedItems.filter((i) => {
            const status = String(i?.status || '').toUpperCase().trim()
            return status !== 'AFTER_SALES' && status !== 'DELETED'
          })
        : []
      const targetItem = validItems.length > 0 ? validItems[0] : null
      const targetHashItem = targetItem?.hash_item || targetItem?.hash_ || targetItem?.hash || item.extra_data?.reservation?.hash_item || item.locator

      if (!targetHashItem) {
        notify(translate('ra.notification.http_error'), { type: 'error' })
        return
      }

      // 3. Armar el body JSON con difference_value
      const diffVal = item.difference_value ?? item.difference ?? 0
      const payload = {
        net_price: diffVal,
      }
      if (afterSalesModal.adjustmentType === 'COST_AND_SALE') {
        payload.final_price = diffVal
      }

      // Si es una conciliación de aéreos, agregar pax_item
      const rawType =
        (typeof conciliation?.conciliation_type === 'object'
          ? conciliation?.conciliation_type?.id
          : conciliation?.conciliation_type)
      const isAirConciliation = String(rawType || '').toUpperCase().trim() === 'AIR'

      if (isAirConciliation) {
        payload.pax_item = [
          {
            ticket_number: String(item.code || '').trim(),
            pricing: {
              fare_amount: diffVal,
              net_price: diffVal,
            },
          },
        ]
      }

      // Pegar al endpoint de postventa según el destino seleccionado
      if (afterSalesModal.destination === 'SAME_RESERVATION') {
        await supplierConciliationServices.createAfterSalesSameReservation(targetHashItem, payload)
      } else {
        await supplierConciliationServices.createAfterSalesNewReservation(targetHashItem, payload)
      }

      // 4. Tras la respuesta OK (200), consultar /reconcile con from_adjustment=true
      await supplierConciliationServices.reconcileItem(item.id, true)

      notify(translate('ra.field.aftersales_success'), { type: 'success' })
      setAfterSalesModal({ open: false, item: null, destination: 'SAME_RESERVATION', adjustmentType: 'COST_ONLY' })
      await loadData()
    } catch (error) {
      notify(translate('ra.notification.http_error'), { type: 'error' })
    } finally {
      setActionLoading(null)
    }
  }

  // Handle No conciliado (Status -> UNRECONCILED)
  const handleUnreconcileConfirm = (itemId) => {
    setConfirmDialog({
      open: true,
      title: translate('ra.field.confirm_unreconcile_title'),
      message: translate('ra.field.confirm_unreconcile_msg'),
      onConfirm: async () => {
        try {
          setActionLoading(itemId)
          if (supplierConciliationServices.updateItem) {
            await supplierConciliationServices.updateItem(itemId, { status: 'UNRECONCILED' })
          }
          notify(translate('ra.notification.updated_success_masculine', { element: translate('ra.field.status') }), { type: 'info' })
          await loadData()
        } catch (error) {
          notify(translate('ra.notification.http_error'), { type: 'error' })
        } finally {
          setActionLoading(null)
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null })
        }
      },
    })
  }

  // Handle Delete line
  const handleDeleteConfirm = (itemId) => {
    setConfirmDialog({
      open: true,
      title: translate('ra.field.confirm_delete_title'),
      message: translate('ra.field.confirm_delete_msg'),
      onConfirm: async () => {
        try {
          setActionLoading(itemId)
          await supplierConciliationServices.deleteItem(itemId)
          notify(translate('ra.notification.deleted', { smart_count: 1 }), { type: 'info' })
          await loadData()
        } catch (error) {
          notify(translate('ra.notification.http_error'), { type: 'error' })
        } finally {
          setActionLoading(null)
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null })
        }
      },
    })
  }

  // Open Retry Conciliation Modal
  const handleOpenRetryModal = () => {
    if (conciliation?.general_status === 'PROCESSING' || conciliation?.status === 'PROCESSING') return

    const targetIds = selectedItemIds.length > 0
      ? selectedItemIds
      : items.filter((i) => i.status !== 'DELETED').map((i) => i.id)

    setRetryModal({
      open: true,
      targetIds,
    })
  }

  // Submit Bulk Retry Conciliation
  const handleRetrySubmit = async () => {
    const targetIds = retryModal.targetIds

    if (!targetIds || targetIds.length === 0) {
      notify(translate('ra.field.no_records'), { type: 'warning' })
      setRetryModal({ open: false, targetIds: [] })
      return
    }

    try {
      setLoading(true)
      await supplierConciliationServices.bulkReconcileItems(targetIds)
      notify(
        translate('ra.field.retry_scheduled_success'),
        { type: 'info' }
      )
      setRetryModal({ open: false, targetIds: [] })
      setSelectedItemIds([])
      navigate('/supplier-conciliation')
    } catch (error) {
      notify(translate('ra.notification.http_error'), { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  // Open OP Modal
  const handleOpenOpModal = () => {
    if (selectedItemIds.length === 0) {
      notify(translate('ra.message.select_at_least_one_element'), { type: 'warning' })
      return
    }

    const conciliationId = conciliation?.id || id
    const defaultObs = translate('ra.field.op_generated_from_conciliation', { id: conciliationId }) || `Orden de pago generada a partir de Conciliación #${conciliationId}`

    setOpModal({
      open: true,
      observations: defaultObs,
      dueDate: '',
      submitting: false,
    })
  }

  // Open Invoice Modal
  const handleOpenInvoiceModal = () => {
    if (selectedItemIds.length === 0) {
      notify(translate('ra.message.select_at_least_one_element'), { type: 'warning' })
      return
    }
    setInvoiceModal((prev) => ({ ...prev, open: true }))
  }

  const handleGenerateInvoiceSubmit = async (formData) => {
    const supplierId = conciliation?.supplier_id || conciliation?.supplier_relation?.id

    if (!supplierId) {
      notify(translate('ra.notification.currency_or_supplier_missing'), { type: 'error' })
      setInvoiceModal({ open: false, data: null, submitting: false })
      return
    }

    try {
      setInvoiceModal((prev) => ({ ...prev, submitting: true }))

      // 1. Consultar /supplier-conciliation/items/associated-items con selectedItemIds
      const associatedRes = await supplierConciliationServices.getBulkAssociatedItems(selectedItemIds)

      // 2. Extraer todos los 'item_id' de la respuesta dict: {"1": [{id: 505, item_id: 100, ...}], ...}
      const allItemSupplierIds = []
      if (associatedRes && typeof associatedRes === 'object') {
        Object.values(associatedRes).forEach((itemList) => {
          if (Array.isArray(itemList)) {
            itemList.forEach((subItem) => {
              const realItemId = subItem?.item_id || subItem?.id
              if (realItemId && !allItemSupplierIds.includes(realItemId)) {
                allItemSupplierIds.push(realItemId)
              }
            })
          }
        })
      }

      if (allItemSupplierIds.length === 0) {
        notify(translate('ra.message.no_records'), { type: 'warning' })
        setInvoiceModal({ open: false, data: null, submitting: false })
        return
      }

      // 3. Consultar /item-supplier/ con supplier_id y item_id__in
      const itemSupplierRes = await itemSupplierServices.getByParam({
        supplier_id: supplierId,
        limit: allItemSupplierIds.length,
        skip: 0,
        item_id__in: allItemSupplierIds.join(','),
      })

      const foundItems = Array.isArray(itemSupplierRes?.data)
        ? itemSupplierRes.data
        : (Array.isArray(itemSupplierRes) ? itemSupplierRes : [])

      if (foundItems.length === 0) {
        notify(translate('ra.notification.no_supplier_items_to_generate_invoice'), { type: 'warning' })
        setInvoiceModal({ open: false, data: null, submitting: false })
        return
      }

      // 4. Para CADA item devuelto, consultar /supplier-billing/item_id/{item_id}
      const itemsToCheckAux = []
      for (const item of foundItems) {
        const itemId = item.item_id || item.id
        try {
          const bills = await supplierBillingServices.checkBilling(itemId)
          if (Array.isArray(bills) && bills.length > 0) {
            itemsToCheckAux.push({
              code: item.detail?.code || '',
              name: item.detail?.name || item.description || `Item ${itemId}`,
              number: item.number || item.item_number || '',
              item_id: itemId,
              bills,
            })
          }
        } catch (err) {
          console.error(`Error checking billing for item ${itemId}:`, err)
        }
      }

      if (itemsToCheckAux.length > 0) {
        // Mostrar modal de advertencia de facturas previas
        setInvoiceModal({ open: false, data: null, submitting: false })
        setCheckInvoiceModal({
          open: true,
          itemsToCheck: itemsToCheckAux,
          formData,
          resolvedItems: foundItems,
          submitting: false,
        })
      } else {
        // Ejecutar la creación de factura directamente
        await executeCreateInvoice(formData, foundItems)
      }
    } catch (error) {
      console.error(error)
      notify(translate('ra.notification.http_error'), { type: 'error' })
      setInvoiceModal({ open: false, data: null, submitting: false })
    }
  }

  // Paso 4: Creación final de la factura (Upload adjunto + POST /supplier-billing/items)
  const executeCreateInvoice = async (formData, resolvedItems) => {
    const supplierId = conciliation?.supplier_id || conciliation?.supplier_relation?.id

    try {
      if (checkInvoiceModal.open) {
        setCheckInvoiceModal((prev) => ({ ...prev, submitting: true }))
      } else {
        setInvoiceModal((prev) => ({ ...prev, submitting: true }))
      }

      // Subir archivo adjunto si existe (POST /invoice/attachment/)
      let attachmentUrl = null
      if (formData?.fileToUpload) {
        try {
          const fileFormData = new FormData()
          fileFormData.append('attachment_file', formData.fileToUpload)
          attachmentUrl = await itemSupplierServices.uploadFileInvoice(fileFormData)
        } catch (uploadErr) {
          notify(translate('ra.notification.upload_file_error'), { type: 'error' })
          setCheckInvoiceModal({ open: false, itemsToCheck: [], formData: null, resolvedItems: [], submitting: false })
          setInvoiceModal({ open: false, data: null, submitting: false })
          return
        }
      }

      // Mapear items_information con item_id y amount
      const itemsInformation = resolvedItems.map((item) => ({
        item_id: item.item_id || item.id,
        amount: Number(item.amount_pending || item.amount || 0),
      }))

      const totalAmount = itemsInformation.reduce((acc, curr) => acc + curr.amount, 0)
      const selectedBase = formData?.basesList?.find((elem) => elem.id === formData.base) || null

      const dateFormatted = formData.date
        ? (dayjs.isDayjs(formData.date) ? formData.date.format('YYYY-MM-DD') : dayjs(formData.date).format('YYYY-MM-DD'))
        : null
      const dueDateFormatted = formData.due_date
        ? (dayjs.isDayjs(formData.due_date) ? formData.due_date.format('YYYY-MM-DD') : dayjs(formData.due_date).format('YYYY-MM-DD'))
        : null
      const accountingDateFormatted = formData.accounting_date
        ? (dayjs.isDayjs(formData.accounting_date) ? formData.accounting_date.format('YYYY-MM-DD') : dayjs(formData.accounting_date).format('YYYY-MM-DD'))
        : null

      const dataToSend = {
        document_type: formData.document_type,
        document_number: formData.document_number,
        currency_id: Number(formData.currency_id),
        supplier_id: Number(supplierId),
        status: 'PENDING',
        base: selectedBase,
        date: dateFormatted,
        due_date: dueDateFormatted,
        accounting_date: accountingDateFormatted,
        items_information: itemsInformation,
        amount: totalAmount,
        attachment_url: attachmentUrl,
      }

      await create(
        'supplier-billing/items',
        { data: dataToSend },
        { returnPromise: true }
      )

      notify('ra.notification.created_success_femenine', {
        type: 'success',
        messageArgs: { element: translate('ra.single_entity.supplier-billing') },
      })

      setSelectedItemIds([])
      await loadData()
    } catch (error) {
      console.error(error)
      notify('ra.notification.create_invoice_process_error_in_conciliation', {
        type: 'error',
      })
    } finally {
      setInvoiceModal({ open: false, data: null, submitting: false })
      setCheckInvoiceModal({ open: false, itemsToCheck: [], formData: null, resolvedItems: [], submitting: false })
    }
  }

  // Submit OP Generation
  const handleGenerateOpSubmit = async () => {
    if (!opModal.dueDate) {
      notify(translate('ra.notification.due_date_required'), { type: 'warning' })
      return
    }

    const supplierId = conciliation?.supplier_id || conciliation?.supplier_relation?.id
    const currencyId = conciliation?.currency_id || conciliation?.currency_relation?.id

    if (!supplierId || !currencyId) {
      notify(translate('ra.notification.currency_or_supplier_missing'), { type: 'error' })
      return
    }

    try {
      setOpModal((prev) => ({ ...prev, submitting: true }))

      // 1. Consultar /supplier-conciliation/items/associated-items
      const associatedRes = await supplierConciliationServices.getBulkAssociatedItems(selectedItemIds)

      // 2. Extraer todos los 'item_id' de la respuesta dict: {"1": [{id: 505, item_id: 100, ...}], ...}
      const allItemSupplierIds = []
      if (associatedRes && typeof associatedRes === 'object') {
        Object.values(associatedRes).forEach((itemList) => {
          if (Array.isArray(itemList)) {
            itemList.forEach((subItem) => {
              const realItemId = subItem?.item_id || subItem?.id
              if (realItemId && !allItemSupplierIds.includes(realItemId)) {
                allItemSupplierIds.push(realItemId)
              }
            })
          }
        })
      }

      if (allItemSupplierIds.length === 0) {
        notify('No supplier-item founds associated to the selected items', { type: 'warning' })
        setOpModal((prev) => ({ ...prev, submitting: false }))
        return
      }

      // 3. Consultar /item-supplier/ con los filtros correspondientes
      const itemSupplierRes = await itemSupplierServices.getByParam({
        status__in: 'UNPAID,PARTIALLY_PAID,PARTIALLY_APPLIED,AVAILABLE',
        supplier_id: supplierId,
        limit: allItemSupplierIds.length,
        skip: 0,
        item_id__in: allItemSupplierIds.join(','),
      })

      const foundItems = Array.isArray(itemSupplierRes?.data)
        ? itemSupplierRes.data
        : (Array.isArray(itemSupplierRes) ? itemSupplierRes : [])

      if (foundItems.length === 0) {
        notify('No supplier-item founds associated to the selected items', { type: 'warning' })
        setOpModal((prev) => ({ ...prev, submitting: false }))
        return
      }

      // 4. Armar el nodo items_information con el campo item_id del objeto item-supplier
      const itemsInformation = foundItems.map((itemSupplier) => ({
        item_id: itemSupplier.item_id || itemSupplier.id,
        amount: Number(itemSupplier.amount_pending || 0),
      }))

      // 5. Armar payload final y consultar /supplier-payment-order/items/
      const payload = {
        supplier_id: Number(supplierId),
        due_date: opModal.dueDate,
        observations: opModal.observations,
        currency_id: Number(currencyId),
        items_information: itemsInformation,
      }

      await supplierPaymentOrderServices.createPaymentOrder(payload)

      notify(translate('ra.field.generate_op_success'), { type: 'success' })
      setOpModal({ open: false, observations: '', dueDate: '', submitting: false })
      setSelectedItemIds([])
      await loadData()
    } catch (error) {
      console.error(error)
      notify(translate('ra.notification.http_error'), { type: 'error' })
    } finally {
      setOpModal((prev) => ({ ...prev, submitting: false }))
    }
  }

  // Number currency formatter
  const formatCurrency = (val) => {
    if (val === null || val === undefined || val === '') return '—'
    const num = Number(val)
    if (isNaN(num)) return '—'
    return num.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  // Active Filter Items List (server-paginated)
  const paginatedItems = items.filter((item) => item.status !== 'DELETED')

  const handleChangePage = async (event, newPage) => {
    setPage(newPage)
    await fetchItemsByFilter(selectedFilter, newPage, rowsPerPage, filterState)
  }

  const handleChangeRowsPerPage = async (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setPage(0)
    await fetchItemsByFilter(selectedFilter, 0, newRowsPerPage, filterState)
  }

  const getTotalCountForFilter = (filterKey) => {
    switch (filterKey) {
      case 'RECONCILED_ALL':
        return totalReconciled
      case 'NOT_FOUND':
        return totalNotFound
      case 'UNRECONCILED':
        return totalUnreconciled
      case 'AMOUNT_MISMATCH':
        return totalMismatch
      case 'ALL':
      default:
        return totalRecords
    }
  }

  // Render Status Badge
  const renderStatusBadge = (status) => {
    switch (status) {
      case 'RECONCILED':
        return (
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '4px',
              bgcolor: '#4caf50',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
            }}
            title={translate('ra.field.reconciled_legend')}
          >
            <CheckIcon sx={{ fontSize: 16 }} />
          </Box>
        )
      case 'RECONCILED_ADJUSTED':
        return (
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '4px',
              bgcolor: '#4caf50',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
            }}
            title={translate('ra.field.reconciled_adjusted_legend')}
          >
            <BuildIcon sx={{ fontSize: 16 }} />
          </Box>
        )
      case 'RECONCILED_MANUALLY':
        return (
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '4px',
              bgcolor: '#4caf50',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
            }}
            title={translate('ra.field.reconciled_manually_legend')}
          >
            <HandshakeIcon sx={{ fontSize: 16 }} />
          </Box>
        )
      case 'RECONCILED_DUPLICATE':
        return (
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '4px',
              bgcolor: '#4caf50',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
            }}
            title={translate('ra.field.reconciled_duplicate_legend')}
          >
            <SyncIcon sx={{ fontSize: 16 }} />
          </Box>
        )
      case 'AMOUNT_MISMATCH':
        return (
          <Tooltip title={translate('ra.field.difference_legend')}>
            <WarningIcon sx={{ color: '#f57c00', fontSize: 18 }} />
          </Tooltip>
        )
      case 'NOT_FOUND':
        return (
          <Tooltip title={translate('ra.field.not_found_legend')}>
            <Typography
              component='span'
              sx={{ color: '#d32f2f', fontWeight: 900, fontSize: '1.3rem', lineHeight: 1, display: 'inline-block' }}
            >
              ?
            </Typography>
          </Tooltip>
        )
      case 'UNRECONCILED':
        return (
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '4px',
              bgcolor: '#d32f2f',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
            }}
            title={translate('ra.field.unreconciled_legend')}
          >
            <ClearIcon sx={{ fontSize: 16 }} />
          </Box>
        )
      case 'DELETED':
        return (
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '4px',
              bgcolor: '#9e9e9e',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
            }}
            title={translate('ra.field.deleted_legend')}
          >
            <RemoveCircleIcon sx={{ fontSize: 16 }} />
          </Box>
        )
      default:
        return (
          <Typography variant='caption' color='textSecondary'>
            {status || '-'}
          </Typography>
        )
    }
  }

  if (loading) {
    return (
      <Container className='content'>
        <Box display='flex' justifyContent='center' p={5}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  // Get Stats Values
  const stats = conciliation?.stats || {}
  const reconciledStatuses = ['RECONCILED', 'RECONCILED_ADJUSTED', 'RECONCILED_MANUALLY', 'RECONCILED_DUPLICATE']

  const totalRecords = stats.TOTAL_RECORDS ?? stats.total_records ?? items.length

  // Calculate total reconciled by summing all reconciled sub-statuses from stats or items
  const reconciledFromStats =
    (stats.RECONCILED ?? stats.reconciled ?? 0) +
    (stats.RECONCILED_ADJUSTED ?? stats.reconciled_adjusted ?? 0) +
    (stats.RECONCILED_MANUALLY ?? stats.reconciled_manually ?? 0) +
    (stats.RECONCILED_DUPLICATE ?? stats.reconciled_duplicate ?? 0)

  const reconciledFromItems = items.filter((i) => reconciledStatuses.includes(i.status)).length

  const totalReconciled = conciliation?.stats
    ? (reconciledFromStats > 0 ? reconciledFromStats : (stats.RECONCILED ?? stats.reconciled ?? reconciledFromItems))
    : reconciledFromItems

  const totalNotFound = stats.NOT_FOUND ?? stats.not_found ?? items.filter((i) => i.status === 'NOT_FOUND').length
  const totalUnreconciled = stats.UNRECONCILED ?? stats.unreconciled ?? items.filter((i) => i.status === 'UNRECONCILED').length
  const totalMismatch = stats.AMOUNT_MISMATCH ?? stats.amount_mismatch ?? items.filter((i) => i.status === 'AMOUNT_MISMATCH').length
  const totalDeleted = stats.DELETED ?? stats.deleted ?? 0

  const clientName =
    client?.long_name ||
    client?.data?.long_name ||
    client?.short_name ||
    conciliation?.client_relation?.long_name ||
    '-'
  const supplierName = conciliation?.supplier_relation?.name || conciliation?.supplier_name || '-'
  const supplierCode = conciliation?.supplier_relation?.code || conciliation?.supplier_code || conciliation?.supplier_id || '-'
  const formattedDate = conciliation?.created_at
    ? new Date(conciliation.created_at).toLocaleDateString('es-AR')
    : '-'
  const currencyCode = conciliation?.currency_relation?.code || conciliation?.currency_relation?.short_name || '-'

  const { name: typeName, icon: typeIcon } = getConciliationTypeInfo(conciliation?.conciliation_type, translate)

  const generalStatus = conciliation?.general_status || conciliation?.status
  let statusText = '-'
  let statusColor = '#757575'
  let statusBgColor = '#f5f5f5'

  if (generalStatus === 'OPEN') {
    statusText = translate('ra.status.open_fem') || translate('ra.status.open')
    statusColor = '#0288d1'
    statusBgColor = '#e0f7fa'
  } else if (generalStatus === 'CLOSED') {
    statusText = translate('ra.status.closed_fem') || translate('ra.status.closed')
    statusColor = '#d32f2f'
    statusBgColor = '#ffebee'
  } else if (generalStatus === 'PROCESSING') {
    statusText = translate('ra.status.processing')
    statusColor = '#ed6c02'
    statusBgColor = '#fff3e0'
  } else if (generalStatus === 'DELETED') {
    statusText = translate('ra.status.deleted_fem') || translate('ra.status.deleted')
    statusColor = '#757575'
    statusBgColor = '#eeeeee'
  } else if (generalStatus === 'COMPLETED') {
    statusText = translate('ra.status.completed_fem') || translate('ra.status.completed')
    statusColor = '#2e7d32'
    statusBgColor = '#e8f5e9'
  } else if (generalStatus) {
    statusText = generalStatus
  }

  const filterSubtitleMap = {
    ALL: translate('ra.field.showing_all'),
    RECONCILED: translate('ra.field.showing_reconciled'),
    NOT_FOUND: translate('ra.field.showing_not_found'),
    UNRECONCILED: translate('ra.field.showing_unreconciled'),
    AMOUNT_MISMATCH: translate('ra.field.showing_differences'),
  }

  const isAllPaginatedSelected =
    paginatedItems.length > 0 &&
    paginatedItems.every((item) => selectedItemIds.includes(item.id))

  return (
    <Container className='content' maxWidth='xl'>
      {/* Top Header Card */}
      <CardCommon
        title={translate('ra.title.supplier_conciliation')}
        headerAction={
          <IconButton
            onClick={loadData}
            disabled={loading}
            sx={{
              color: '#5c6ac4',
            }}
          >
            {loading ? <CircularProgress size={32} /> : <RefreshIcon sx={{ fontSize: 36 }} />}
          </IconButton>
        }
      >
        <Box display='flex' flexDirection='column' gap={2} p={1}>
          {/* Breadcrumb & Actions Bar */}
          <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={2}>
            <Typography variant='caption' color='textSecondary'>
              {translate('ra.title.suppliers')} &gt; {translate('ra.title.supplier_conciliation')} &gt; {translate('ra.action.see_details')}
            </Typography>
            <Box display='flex' gap={1} alignItems='center' flexWrap='wrap'>
              {!isReadOnly && (
                <>
                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={<ReplayIcon fontSize='small' />}
                    onClick={handleOpenRetryModal}
                    disabled={conciliation?.general_status === 'PROCESSING'}
                    style={{ textTransform: 'none' }}
                  >
                    {translate('ra.action.reprocess_conciliation')}
                  </Button>
                  <Button
                    variant='outlined'
                    size='small'
                    onClick={handleOpenOpModal}
                    disabled={conciliation?.general_status === 'PROCESSING'}
                    style={{ textTransform: 'none' }}
                  >
                    {translate('ra.action.generate_op')}
                  </Button>
                  <Button
                    variant='outlined'
                    size='small'
                    onClick={handleOpenInvoiceModal}
                    disabled={conciliation?.general_status === 'PROCESSING'}
                    style={{ textTransform: 'none' }}
                  >
                    {translate('ra.action.generate_invoice')}
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {/* Card 1: Conciliation Header Info & Stat Boxes */}
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, width: '100%' }}>
            <Box sx={{ width: '100%' }}>
              <Grid container spacing={1.5} mb={1.5} columns={{ xs: 12, sm: 12, md: 14, lg: 14, xl: 14 }}>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant='caption' color='textSecondary' display='block'>
                    {translate('ra.field.client') || 'Cliente'}
                  </Typography>
                  <Typography variant='subtitle2' fontWeight='bold'>
                    {clientLoading ? <CircularProgress size={14} /> : clientName}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant='caption' color='textSecondary' display='block'>
                    {translate('ra.field.supplier')}
                  </Typography>
                  <Typography variant='subtitle2' fontWeight='bold'>
                    {supplierName}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant='caption' color='textSecondary' display='block'>
                    {translate('ra.field.code')}
                  </Typography>
                  <Typography variant='subtitle2' fontWeight='bold'>
                    {supplierCode}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant='caption' color='textSecondary' display='block'>
                    {translate('ra.field.type')}
                  </Typography>
                  <Box display='flex' alignItems='center' gap={0.75} mt={0.25}>
                    {typeIcon}
                    <Typography variant='subtitle2' fontWeight='bold'>
                      {typeName}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant='caption' color='textSecondary' display='block'>
                    {translate('ra.field.date')}
                  </Typography>
                  <Typography variant='subtitle2' fontWeight='bold'>
                    {formattedDate}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant='caption' color='textSecondary' display='block'>
                    {translate('ra.field.currency')}
                  </Typography>
                  <Typography variant='subtitle2' fontWeight='bold'>
                    {currencyCode}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant='caption' color='textSecondary' display='block'>
                    {translate('ra.field.general_status') || 'Estado'}
                  </Typography>
                  <Box mt={0.25} display='flex' alignItems='center'>
                    <Chip
                      label={statusText}
                      size='small'
                      sx={{
                        bgcolor: statusBgColor,
                        color: statusColor,
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        border: `1px solid ${statusColor}40`,
                        height: '24px',
                      }}
                    />
                    {!isReadOnly && hasPermissionFunction('supplier-conciliation', 'edit') && (
                      <Tooltip title={translate('ra.title.update_status')}>
                        <EditIcon
                          onClick={() => setChangeStatusDialogOpen(true)}
                          style={{
                            cursor: 'pointer',
                            fontSize: '18px',
                            color: '#6f7ce0',
                            marginLeft: '8px',
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Stat Boxes */}
              <Grid container spacing={1.5} columns={18}>
                {/* Box 1: Total registros */}
                <Grid item xs={12} sm={3}>
                  <Paper
                    elevation={0}
                    onClick={() => handleFilterClick('ALL')}
                    sx={{
                      p: 0.75,
                      textAlign: 'center',
                      borderRadius: 1.5,
                      bgcolor: '#e8f0fe',
                      border: selectedFilter === 'ALL' ? '2px solid #0d6efd' : '1px solid #b6d4fe',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': { opacity: 0.9, transform: 'translateY(-1px)' },
                    }}
                  >
                    <Typography variant='caption' sx={{ color: '#495057', fontSize: '0.75rem' }} display='block'>
                      {translate('ra.field.total_records')}
                    </Typography>
                    <Typography variant='subtitle1' fontWeight='bold' sx={{ color: '#0d6efd', lineHeight: 1.2 }}>
                      {totalRecords}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Box 2: Total conciliado */}
                <Grid item xs={12} sm={3}>
                  <Paper
                    elevation={0}
                    onClick={() => handleFilterClick('RECONCILED_ALL')}
                    sx={{
                      p: 0.75,
                      textAlign: 'center',
                      borderRadius: 1.5,
                      bgcolor: '#e6f4ea',
                      border: selectedFilter === 'RECONCILED_ALL' ? '2px solid #137333' : '1px solid #a8dab5',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': { opacity: 0.9, transform: 'translateY(-1px)' },
                    }}
                  >
                    <Typography variant='caption' sx={{ color: '#495057', fontSize: '0.75rem' }} display='block'>
                      {translate('ra.field.total_reconciled')}
                    </Typography>
                    <Typography variant='subtitle1' fontWeight='bold' sx={{ color: '#137333', lineHeight: 1.2 }}>
                      {totalReconciled}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Box 3: Con diferencias (Yellow) */}
                <Grid item xs={12} sm={3}>
                  <Paper
                    elevation={0}
                    onClick={() => handleFilterClick('AMOUNT_MISMATCH')}
                    sx={{
                      p: 0.75,
                      textAlign: 'center',
                      borderRadius: 1.5,
                      bgcolor: '#fef7e0',
                      border: selectedFilter === 'AMOUNT_MISMATCH' ? '2px solid #b06000' : '1px solid #fde293',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': { opacity: 0.9, transform: 'translateY(-1px)' },
                    }}
                  >
                    <Typography variant='caption' sx={{ color: '#495057', fontSize: '0.75rem' }} display='block'>
                      {translate('ra.field.with_differences')}
                    </Typography>
                    <Typography variant='subtitle1' fontWeight='bold' sx={{ color: '#b06000', lineHeight: 1.2 }}>
                      {totalMismatch}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Box 4: No encontrados (Red) */}
                <Grid item xs={12} sm={3}>
                  <Paper
                    elevation={0}
                    onClick={() => handleFilterClick('NOT_FOUND')}
                    sx={{
                      p: 0.75,
                      textAlign: 'center',
                      borderRadius: 1.5,
                      bgcolor: '#fce8e6',
                      border: selectedFilter === 'NOT_FOUND' ? '2px solid #c5221f' : '1px solid #f5c2c7',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': { opacity: 0.9, transform: 'translateY(-1px)' },
                    }}
                  >
                    <Typography variant='caption' sx={{ color: '#495057', fontSize: '0.75rem' }} display='block'>
                      {translate('ra.field.not_found')}
                    </Typography>
                    <Typography variant='subtitle1' fontWeight='bold' sx={{ color: '#c5221f', lineHeight: 1.2 }}>
                      {totalNotFound}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Box 5: No conciliados (More saturated red) */}
                <Grid item xs={12} sm={3}>
                  <Paper
                    elevation={0}
                    onClick={() => handleFilterClick('UNRECONCILED')}
                    sx={{
                      p: 0.75,
                      textAlign: 'center',
                      borderRadius: 1.5,
                      bgcolor: '#ffcdd2',
                      border: selectedFilter === 'UNRECONCILED' ? '2px solid #b71c1c' : '1px solid #ef9a9a',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': { opacity: 0.9, transform: 'translateY(-1px)' },
                    }}
                  >
                    <Typography variant='caption' sx={{ color: '#495057', fontSize: '0.75rem' }} display='block'>
                      {translate('ra.field.unreconciled')}
                    </Typography>
                    <Typography variant='subtitle1' fontWeight='bold' sx={{ color: '#b71c1c', lineHeight: 1.2 }}>
                      {totalUnreconciled}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Box 5: Eliminados (Grey) - solo informativo, no clickeable */}
                <Grid item xs={12} sm={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 0.75,
                      textAlign: 'center',
                      borderRadius: 1.5,
                      bgcolor: '#f5f5f5',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <Typography variant='caption' sx={{ color: '#495057', fontSize: '0.75rem' }} display='block'>
                      {translate('ra.field.total_deleted')}
                    </Typography>
                    <Typography variant='subtitle1' fontWeight='bold' sx={{ color: '#616161', lineHeight: 1.2 }}>
                      {totalDeleted}
                    </Typography>
                  </Paper>
                </Grid>

              </Grid>
            </Box>
          </Paper>

          {/* Card 1.5: Status Reference Legend & Filters (Below general stats) */}
          <Paper elevation={1} sx={{ p: 1.5, px: 2, borderRadius: 2 }}>
            <Box display='flex' alignItems='center' flexWrap='wrap' gap={1.5}>
              <Typography variant='caption' fontWeight='bold' color='textSecondary' sx={{ mr: 1 }}>
                {translate('ra.field.states_legend')}
              </Typography>

              {/* 1. RECONCILED */}
              <Box
                onClick={() => handleFilterClick('RECONCILED')}
                display='flex'
                alignItems='center'
                gap={0.75}
                sx={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: selectedFilter === 'RECONCILED' ? '1.5px solid #2e7d32' : '1px solid #e0e0e0',
                  backgroundColor: selectedFilter === 'RECONCILED' ? '#e8f5e9' : 'transparent',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  transition: 'all 0.15s ease',
                }}
              >
                <Box sx={{ width: 16, height: 16, borderRadius: '3px', bgcolor: '#4caf50', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <CheckIcon sx={{ fontSize: 12 }} />
                </Box>
                <Typography variant='caption' fontWeight={selectedFilter === 'RECONCILED' ? 'bold' : 'normal'}>
                  {translate('ra.field.reconciled_legend')}
                </Typography>
              </Box>

              {/* 2. RECONCILED_ADJUSTED */}
              <Box
                onClick={() => handleFilterClick('RECONCILED_ADJUSTED')}
                display='flex'
                alignItems='center'
                gap={0.75}
                sx={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: selectedFilter === 'RECONCILED_ADJUSTED' ? '1.5px solid #2e7d32' : '1px solid #e0e0e0',
                  backgroundColor: selectedFilter === 'RECONCILED_ADJUSTED' ? '#e8f5e9' : 'transparent',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  transition: 'all 0.15s ease',
                }}
              >
                <Box sx={{ width: 16, height: 16, borderRadius: '3px', bgcolor: '#4caf50', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <BuildIcon sx={{ fontSize: 12 }} />
                </Box>
                <Typography variant='caption' fontWeight={selectedFilter === 'RECONCILED_ADJUSTED' ? 'bold' : 'normal'}>
                  {translate('ra.field.reconciled_adjusted_legend')}
                </Typography>
              </Box>

              {/* 3. RECONCILED_MANUALLY */}
              <Box
                onClick={() => handleFilterClick('RECONCILED_MANUALLY')}
                display='flex'
                alignItems='center'
                gap={0.75}
                sx={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: selectedFilter === 'RECONCILED_MANUALLY' ? '1.5px solid #2e7d32' : '1px solid #e0e0e0',
                  backgroundColor: selectedFilter === 'RECONCILED_MANUALLY' ? '#e8f5e9' : 'transparent',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  transition: 'all 0.15s ease',
                }}
              >
                <Box sx={{ width: 16, height: 16, borderRadius: '3px', bgcolor: '#4caf50', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <HandshakeIcon sx={{ fontSize: 12 }} />
                </Box>
                <Typography variant='caption' fontWeight={selectedFilter === 'RECONCILED_MANUALLY' ? 'bold' : 'normal'}>
                  {translate('ra.field.reconciled_manually_legend')}
                </Typography>
              </Box>

              {/* 4. RECONCILED_DUPLICATE */}
              <Box
                onClick={() => handleFilterClick('RECONCILED_DUPLICATE')}
                display='flex'
                alignItems='center'
                gap={0.75}
                sx={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: selectedFilter === 'RECONCILED_DUPLICATE' ? '1.5px solid #2e7d32' : '1px solid #e0e0e0',
                  backgroundColor: selectedFilter === 'RECONCILED_DUPLICATE' ? '#e8f5e9' : 'transparent',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  transition: 'all 0.15s ease',
                }}
              >
                <Box sx={{ width: 16, height: 16, borderRadius: '3px', bgcolor: '#4caf50', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <SyncIcon sx={{ fontSize: 12 }} />
                </Box>
                <Typography variant='caption' fontWeight={selectedFilter === 'RECONCILED_DUPLICATE' ? 'bold' : 'normal'}>
                  {translate('ra.field.reconciled_duplicate_legend')}
                </Typography>
              </Box>

              {/* 5. AMOUNT_MISMATCH */}
              <Box
                onClick={() => handleFilterClick('AMOUNT_MISMATCH')}
                display='flex'
                alignItems='center'
                gap={0.75}
                sx={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: selectedFilter === 'AMOUNT_MISMATCH' ? '1.5px solid #ef6c00' : '1px solid #e0e0e0',
                  backgroundColor: selectedFilter === 'AMOUNT_MISMATCH' ? '#fff3e0' : 'transparent',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  transition: 'all 0.15s ease',
                }}
              >
                <WarningIcon sx={{ color: '#f57c00', fontSize: 18 }} />
                <Typography variant='caption' fontWeight={selectedFilter === 'AMOUNT_MISMATCH' ? 'bold' : 'normal'}>
                  {translate('ra.field.difference_legend')}
                </Typography>
              </Box>

              {/* 6. NOT_FOUND */}
              <Box
                onClick={() => handleFilterClick('NOT_FOUND')}
                display='flex'
                alignItems='center'
                gap={0.75}
                sx={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: selectedFilter === 'NOT_FOUND' ? '1.5px solid #c62828' : '1px solid #e0e0e0',
                  backgroundColor: selectedFilter === 'NOT_FOUND' ? '#ffebee' : 'transparent',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  transition: 'all 0.15s ease',
                }}
              >
                <Typography variant='caption' fontWeight={900} sx={{ color: '#d32f2f', fontSize: '1.1rem', lineHeight: 1 }}>?</Typography>
                <Typography variant='caption' fontWeight={selectedFilter === 'NOT_FOUND' ? 'bold' : 'normal'}>
                  {translate('ra.field.not_found_legend')}
                </Typography>
              </Box>

              {/* 7. UNRECONCILED */}
              <Box
                onClick={() => handleFilterClick('UNRECONCILED')}
                display='flex'
                alignItems='center'
                gap={0.75}
                sx={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: selectedFilter === 'UNRECONCILED' ? '1.5px solid #c62828' : '1px solid #e0e0e0',
                  backgroundColor: selectedFilter === 'UNRECONCILED' ? '#ffebee' : 'transparent',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  transition: 'all 0.15s ease',
                }}
              >
                <Box sx={{ width: 16, height: 16, borderRadius: '3px', bgcolor: '#d32f2f', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <ClearIcon sx={{ fontSize: 12 }} />
                </Box>
                <Typography variant='caption' fontWeight={selectedFilter === 'UNRECONCILED' ? 'bold' : 'normal'}>
                  {translate('ra.field.unreconciled_legend')}
                </Typography>
              </Box>
            </Box>
          </Paper>


          {/* Card 2: Table Section */}
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={1} mb={2}>
              <Typography variant='subtitle1' fontWeight={500} sx={{ fontSize: '1rem' }}>
                {translate('ra.title.supplier_conciliation_detail')}
              </Typography>

              <Box display='flex' alignItems='center' gap={1}>
                {/* Botón Agregar Filtro */}
                <Button
                  size='small'
                  startIcon={<FilterListIcon />}
                  onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                  sx={{ textTransform: 'none' }}
                >
                  {translate('ra.action.add_filter')}
                </Button>

                <Menu
                  anchorEl={filterMenuAnchor}
                  open={Boolean(filterMenuAnchor)}
                  onClose={() => setFilterMenuAnchor(null)}
                >
                  {[
                    { source: 'code', name: translate('ra.field.locator') || 'Localizador' },
                    { source: 'status', name: translate('ra.field.status') || 'Estado' },
                    { source: 'is_billed', name: translate('ra.field.has_invoice') || 'Posee factura' },
                    { source: 'is_paid', name: translate('ra.field.is_paid') || 'Está pagado' },
                    { source: 'reservation_number', name: translate('ra.field.short_reservation_number') || 'Nro. reserva' },
                    { source: 'bases', name: translate('ra.field.billing_base') || 'Base Facturación' },
                    { source: 'work_unit_id', name: translate('ra.field.work_unit') || 'Unidad de Trabajo' },
                    { source: 'difference', name: translate('ra.field.difference') || 'Diferencia' },
                  ].map((opt) => (
                    !displayedFilters[opt.source] && (
                      <MenuItem
                        key={opt.source}
                        onClick={() => {
                          setDisplayedFilters((prev) => ({ ...prev, [opt.source]: true }))
                          if (opt.source === 'is_billed' && (filterState.is_billed === '' || filterState.is_billed === undefined)) {
                            setFilterState((prev) => ({ ...prev, is_billed: true }))
                          }
                          if (opt.source === 'is_paid' && (filterState.is_paid === '' || filterState.is_paid === undefined)) {
                            setFilterState((prev) => ({ ...prev, is_paid: true }))
                          }
                          setFilterMenuAnchor(null)
                        }}
                      >
                        {opt.name}
                      </MenuItem>
                    )
                  ))}

                  {Object.values(displayedFilters).some(Boolean) && (
                    <MenuItem onClick={handleClearAllFilters}>
                      {translate('ra.action.remove_all_filters') || 'Remover todos los filtros'}
                    </MenuItem>
                  )}
                </Menu>

                {/* Botón Exportar */}
                <LoadingButton
                  size='small'
                  onClick={handleExport}
                  loading={exportLoading}
                  loadingPosition='start'
                  startIcon={<DownloadIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {translate('ra.action.export') || 'Exportar'}
                </LoadingButton>
              </Box>
            </Box>

            {/* Filter Form Bar Container */}
            {Object.values(displayedFilters).some(Boolean) && (
              <Box
                sx={{
                  backgroundColor: '#f8f9fa',
                  p: 2,
                  borderRadius: 1.5,
                  mb: 2,
                  border: '1px solid #e0e0e0',
                }}
              >
                <Grid container spacing={2} alignItems='flex-start'>
                  {/* 1. Localizador (code) */}
                  {displayedFilters.code && (
                    <Grid item xs={12} sm={6} md={3}>
                      <RemoveFilterField
                        label={translate('ra.field.locator') || 'Localizador'}
                        onRemove={() => handleRemoveFilter('code')}
                      >
                        <TextField
                          size='small'
                          fullWidth
                          value={filterState.code}
                          onChange={(e) => setFilterState((prev) => ({ ...prev, code: e.target.value }))}
                          placeholder={translate('ra.field.locator')}
                        />
                      </RemoveFilterField>
                    </Grid>
                  )}

                  {/* 2. Estado (status) */}
                  {displayedFilters.status && (
                    <Grid item xs={12} sm={6} md={4}>
                      <RemoveFilterField
                        label={translate('ra.field.status') || 'Estado'}
                        onRemove={() => handleRemoveFilter('status')}
                      >
                        <Autocomplete
                          multiple
                          size='small'
                          fullWidth
                          disableCloseOnSelect
                          options={STATUS_FILTER_OPTIONS}
                          getOptionLabel={(option) => translate(option.labelKey) || option.defaultLabel}
                          isOptionEqualToValue={(option, val) => option.value === (val?.value || val)}
                          value={STATUS_FILTER_OPTIONS.filter((opt) => (filterState.status || []).includes(opt.value))}
                          onChange={(e, newValues) => {
                            setFilterState((prev) => ({
                              ...prev,
                              status: newValues.map((v) => v.value),
                            }))
                          }}
                          renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.value}>
                              <Checkbox
                                size='small'
                                checked={selected}
                                sx={{ mr: 1, p: 0.5 }}
                              />
                              {translate(option.labelKey) || option.defaultLabel}
                            </li>
                          )}
                          renderTags={(tagValue, getTagProps) =>
                            tagValue.map((option, index) => (
                              <Chip
                                {...getTagProps({ index })}
                                key={option.value}
                                label={translate(option.labelKey) || option.defaultLabel}
                                size='small'
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size='small'
                              placeholder={(filterState.status || []).length === 0 ? (translate('ra.action.select_with_dots') || 'Seleccionar...') : ''}
                            />
                          )}
                        />
                      </RemoveFilterField>
                    </Grid>
                  )}

                  {/* 3. Posee factura (is_billed) */}
                  {displayedFilters.is_billed && (
                    <Grid item xs={12} sm={6} md={2.5}>
                      <RemoveFilterField
                        label={translate('ra.field.has_invoice') || 'Posee factura'}
                        onRemove={() => handleRemoveFilter('is_billed')}
                      >
                        <Box display='flex' alignItems='center' height='40px'>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(filterState.is_billed)}
                                onChange={(e) => setFilterState((prev) => ({ ...prev, is_billed: e.target.checked }))}
                                color='primary'
                                size='small'
                              />
                            }
                            label={
                              <Typography variant='body2' sx={{ fontSize: '0.85rem' }}>
                                {filterState.is_billed
                                  ? (translate('ra.message.yes') || 'Sí')
                                  : (translate('ra.message.no') || 'No')}
                              </Typography>
                            }
                            sx={{ m: 0 }}
                          />
                        </Box>
                      </RemoveFilterField>
                    </Grid>
                  )}

                  {/* 4. Está pagado (is_paid) */}
                  {displayedFilters.is_paid && (
                    <Grid item xs={12} sm={6} md={2.5}>
                      <RemoveFilterField
                        label={translate('ra.field.is_paid') || 'Está pagado'}
                        onRemove={() => handleRemoveFilter('is_paid')}
                      >
                        <Box display='flex' alignItems='center' height='40px'>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(filterState.is_paid)}
                                onChange={(e) => setFilterState((prev) => ({ ...prev, is_paid: e.target.checked }))}
                                color='primary'
                                size='small'
                              />
                            }
                            label={
                              <Typography variant='body2' sx={{ fontSize: '0.85rem' }}>
                                {filterState.is_paid
                                  ? (translate('ra.message.yes') || 'Sí')
                                  : (translate('ra.message.no') || 'No')}
                              </Typography>
                            }
                            sx={{ m: 0 }}
                          />
                        </Box>
                      </RemoveFilterField>
                    </Grid>
                  )}

                  {/* 5. Nro. Reserva (reservation_number) */}
                  {displayedFilters.reservation_number && (
                    <Grid item xs={12} sm={6} md={3}>
                      <RemoveFilterField
                        label={translate('ra.field.short_reservation_number') || 'Nro. reserva'}
                        onRemove={() => handleRemoveFilter('reservation_number')}
                      >
                        <TextField
                          size='small'
                          fullWidth
                          value={filterState.reservation_number}
                          onChange={(e) => setFilterState((prev) => ({ ...prev, reservation_number: e.target.value }))}
                          placeholder={translate('ra.field.short_reservation_number')}
                        />
                      </RemoveFilterField>
                    </Grid>
                  )}

                  {/* 6. Base Facturación (bases) */}
                  {displayedFilters.bases && (
                    <Grid item xs={12} sm={6} md={3}>
                      <RemoveFilterField
                        label={translate('ra.field.billing_base') || 'Base Facturación'}
                        onRemove={() => handleRemoveFilter('bases')}
                      >
                        <FormControl size='small' fullWidth>
                          <Select
                            value={filterState.bases}
                            onChange={(e) => setFilterState((prev) => ({ ...prev, bases: e.target.value }))}
                            displayEmpty
                          >
                            <MenuItem value='' disabled sx={{ color: 'text.secondary' }}>
                              {translate('ra.action.select_with_dots') || 'Seleccionar...'}
                            </MenuItem>
                            {basesList.map((b) => (
                              <MenuItem key={b.id} value={b.id}>
                                {b.name || `Base #${b.id}`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </RemoveFilterField>
                    </Grid>
                  )}

                  {/* 7. Unidad de Trabajo (work_unit_id) */}
                  {displayedFilters.work_unit_id && (
                    <Grid item xs={12} sm={6} md={3.5}>
                      <RemoveFilterField
                        label={translate('ra.field.work_unit') || 'Unidad de Trabajo'}
                        onRemove={() => handleRemoveFilter('work_unit_id')}
                      >
                        <Autocomplete
                          size='small'
                          fullWidth
                          options={
                            selectedWorkUnitObj && !workUnitsList.some((w) => w.id === selectedWorkUnitObj.id)
                              ? [selectedWorkUnitObj, ...workUnitsList]
                              : workUnitsList
                          }
                          loading={loadingWorkUnits}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') return option
                            return option.long_name || option.short_name || option.name || String(option.id)
                          }}
                          isOptionEqualToValue={(option, val) => option?.id === (val?.id || val)}
                          value={selectedWorkUnitObj || workUnitsList.find((w) => w.id === filterState.work_unit_id) || null}
                          onChange={(e, newValue) => {
                            setSelectedWorkUnitObj(newValue || null)
                            setFilterState((prev) => ({
                              ...prev,
                              work_unit_id: newValue ? newValue.id : '',
                            }))
                          }}
                          onInputChange={(e, newInputValue) => {
                            if (e && e.type === 'change') {
                              const term = newInputValue.trim()
                              if (searchTimeoutRef.current) {
                                clearTimeout(searchTimeoutRef.current)
                              }
                              if (term.length >= 3) {
                                searchTimeoutRef.current = setTimeout(() => {
                                  loadWorkUnits(term)
                                }, 300)
                              } else if (term.length === 0) {
                                loadWorkUnits('')
                              }
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size='small'
                              placeholder={translate('ra.message.enter_at_least', { num: 3 })}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingWorkUnits ? <CircularProgress color='inherit' size={18} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </RemoveFilterField>
                    </Grid>
                  )}

                  {/* 8. Diferencia (min_difference_value / max_difference_value) */}
                  {displayedFilters.difference && (
                    <Grid item xs={12} sm={6} md={4}>
                      <RemoveFilterField
                        label={translate('ra.field.difference') || 'Diferencia'}
                        onRemove={() => handleRemoveFilter('difference')}
                      >
                        <Box display='flex' gap={1} alignItems='center' width='100%'>
                          <TextField
                            size='small'
                            type='number'
                            placeholder={translate('ra.field.from') || 'Desde'}
                            value={filterState.min_difference_value}
                            onChange={(e) => setFilterState((prev) => ({ ...prev, min_difference_value: e.target.value }))}
                            sx={{ flex: 1 }}
                          />
                          <TextField
                            size='small'
                            type='number'
                            placeholder={translate('ra.field.to') || 'Hasta'}
                            value={filterState.max_difference_value}
                            onChange={(e) => setFilterState((prev) => ({ ...prev, max_difference_value: e.target.value }))}
                            sx={{ flex: 1 }}
                          />
                        </Box>
                      </RemoveFilterField>
                    </Grid>
                  )}
                </Grid>

                {/* Buttons Filtrar / Cerrar */}
                <Box display='flex' justifyContent='flex-end' gap={1.5} mt={2}>
                  <Button
                    variant='outlined'
                    color='primary'
                    size='small'
                    onClick={handleApplyFilters}
                    sx={{ textTransform: 'none' }}
                  >
                    {translate('ra.action.filter') || 'Filtrar'}
                  </Button>
                  <Button
                    variant='outlined'
                    color='inherit'
                    size='small'
                    onClick={handleClearAllFilters}
                    sx={{ textTransform: 'none' }}
                  >
                    {translate('ra.action.close') || 'Cerrar'}
                  </Button>
                </Box>
              </Box>
            )}

            <TableContainer sx={{ position: 'relative' }}>
              {itemsLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress size={32} />
                </Box>
              )}

              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    {!isReadOnly && (
                      <TableCell padding='checkbox'>
                        <Checkbox
                          size='small'
                          checked={isAllPaginatedSelected}
                          onChange={handleSelectAllClick}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>{translate('ra.field.locator')}</TableCell>
                    <TableCell align='center' sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>{translate('ra.field.product')}</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>{translate('ra.field.supplier_value')}</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>{translate('ra.field.b2_value')}</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>{translate('ra.field.difference')}</TableCell>
                    <TableCell align='center' sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>{translate('ra.field.has_invoice')}</TableCell>
                    <TableCell align='center' sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>{translate('ra.field.is_paid')}</TableCell>
                    <TableCell align='center' sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>{translate('ra.field.short_reservation_number')}</TableCell>
                    <TableCell align='center' sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>{translate('ra.field.billing_base')}</TableCell>
                    <TableCell align='center' sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>{translate('ra.field.ut')}</TableCell>
                    {/* <TableCell align='center' sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>{translate('ra.field.seller')}</TableCell> */}
                    {!isReadOnly && (
                      <TableCell align='center' sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>{translate('ra.field.actions')}</TableCell>
                    )}
                    <TableCell align='center' sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                      <Box display='inline-flex' alignItems='center' justify-content='center' gap={0.5}>
                        {translate('ra.field.status')}
                        {selectedFilter !== 'ALL' && (
                          <Tooltip title={filterSubtitleMap[selectedFilter]}>
                            <FilterAltIcon fontSize='small' sx={{ color: '#1976d2' }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isReadOnly ? 11 : 13} align='center' sx={{ py: 3 }}>
                        {translate('ra.field.no_records')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedItems.map((item, idx) => {
                      const diffVal = item.difference_value ?? item.difference
                      const diffNum = Number(diffVal)
                      const isZeroDiff = diffVal !== null && diffVal !== undefined && Math.abs(diffNum) < 0.001

                      const resData = item.extra_data?.reservation
                      const resValue = resData?.value
                      const resId = resData?.id

                      const baseData = item.extra_data?.bases
                      const baseValue = baseData?.value
                      const baseId = baseData?.id

                      const isNotFound = item.status === 'NOT_FOUND'
                      const isItemSelected = selectedItemIds.includes(item.id)

                      return (
                        <TableRow key={item.id || idx} hover>
                          {!isReadOnly && (
                            <TableCell padding='checkbox'>
                              <Checkbox
                                size='small'
                                checked={isItemSelected}
                                onChange={() => handleItemSelectClick(item.id)}
                              />
                            </TableCell>
                          )}
                          {/* Localizador */}
                          <TableCell sx={{ color: isNotFound ? '#d32f2f' : 'inherit', fontWeight: 'normal' }}>
                            {item.code || item.locator || '-'}
                          </TableCell>

                          {/* Producto */}
                          <TableCell align='center'>
                            {(() => {
                              const productType = item?.extra_data?.product_type
                              const refTable =
                                productType?.ref_table_detail ||
                                (typeof productType === 'string' ? productType : null)
                              const icon = refTable ? iconAccordingProduct(refTable) : null
                              const name =
                                productType?.long_name ||
                                productType?.short_name ||
                                productType?.name ||
                                (typeof productType === 'string' ? productType : '')

                              if (!icon && !name) return '—'

                              return (
                                <Tooltip title={name || refTable || ''}>
                                  <Box display='inline-flex' alignItems='center' justifyContent='center'>
                                    {icon || name}
                                  </Box>
                                </Tooltip>
                              )
                            })()}
                          </TableCell>


                          {/* Val. proveedor */}
                          <TableCell align='right'>{formatCurrency(item.supplier_value)}</TableCell>
                          {/* Val. B2 */}
                          <TableCell align='right'>{formatCurrency(item.b2_value)}</TableCell>
                          {/* Diferencia */}
                          <TableCell align='right'>
                            {diffVal !== null && diffVal !== undefined ? (
                              <Typography
                                variant='body2'
                                fontWeight='bold'
                                component='span'
                                sx={{ color: isZeroDiff ? '#2e7d32' : '#d32f2f' }}
                              >
                                {formatCurrency(diffVal)}
                              </Typography>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          {/* Posee factura */}
                          <TableCell align='center'>{(item.is_billed ?? item.is_billing) ? translate('ra.message.yes') : translate('ra.message.no')}</TableCell>
                          {/* Está pagado */}
                          <TableCell align='center'>{item.is_paid ? translate('ra.message.yes') : translate('ra.message.no')}</TableCell>
                          {/* Nro. Reserva (link -> /show) */}
                          <TableCell align='center'>
                            {resValue && resId ? (
                              <Typography
                                component='a'
                                href={`/#/reservation/${resId}/show`}
                                target='_blank'
                                rel='noopener noreferrer'
                                sx={{ color: '#3f51b5', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                              >
                                {resValue}
                              </Typography>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          {/* Base Facturación */}
                          <TableCell align='center'>
                            {baseValue ? (
                              baseId ? (
                                <Typography
                                  component='a'
                                  href={`/#/bases/${baseId}/show`}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  sx={{ color: '#3f51b5', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                                >
                                  {baseValue}
                                </Typography>
                              ) : (
                                baseValue
                              )
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          {/* UT */}
                          <TableCell align='center'>{item.extra_data?.work_unit?.value || '—'}</TableCell>
                          {/* Vendedor (Comentado) */}
                          {/* <TableCell align='center'>{item.extra_data?.seller?.value || '—'}</TableCell> */}
                          {/* Acciones */}
                          {!isReadOnly && (
                            <TableCell align='center'>
                              <Box display='flex' justifyContent='center' gap={0.5}>
                                {/* 1. Forzar conciliación (Handshake icon) */}
                                <Tooltip title={translate('ra.action.force_reconcile')}>
                                  <IconButton
                                    size='small'
                                    sx={{ color: '#5c6ac4' }}
                                    disabled={actionLoading === item.id}
                                    onClick={() => handleForceReconcile(item.id)}
                                  >
                                    {actionLoading === item.id ? <CircularProgress size={16} /> : <HandshakeIcon sx={{ fontSize: 18 }} />}
                                  </IconButton>
                                </Tooltip>

                                {/* 2. Reprocesar (Replay icon) */}
                                <Tooltip title={translate('ra.action.reprocess_conciliation_item')}>
                                  <IconButton
                                    size='small'
                                    sx={{ color: '#5c6ac4' }}
                                    disabled={actionLoading === item.id}
                                    onClick={() => handleReprocessItem(item.id)}
                                  >
                                    {actionLoading === item.id ? <CircularProgress size={16} /> : <ReplayIcon sx={{ fontSize: 18 }} />}
                                  </IconButton>
                                </Tooltip>

                                {/* 2. Ajustar costo/venta (Build icon) */}
                                <Tooltip title={translate('ra.action.adjust_cost_sale')}>
                                  <span>
                                    <IconButton
                                      size='small'
                                      sx={{ color: '#5c6ac4' }}
                                      disabled={actionLoading === item.id}
                                      onClick={() => handleOpenAfterSales(item)}
                                    >
                                      <BuildIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>

                                {/* 3. No conciliar (Block icon) */}
                                <Tooltip title={translate('ra.action.unreconcile')}>
                                  <IconButton
                                    size='small'
                                    sx={{ color: '#5c6ac4' }}
                                    disabled={actionLoading === item.id}
                                    onClick={() => handleUnreconcileConfirm(item.id)}
                                  >
                                    <BlockIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>

                                {/* 4. Eliminar línea (Delete icon - Red) */}
                                <Tooltip title={translate('ra.action.delete_line')}>
                                  <IconButton
                                    size='small'
                                    sx={{ color: '#d32f2f' }}
                                    disabled={actionLoading === item.id}
                                    onClick={() => handleDeleteConfirm(item.id)}
                                  >
                                    <DeleteIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          )}



                          {/* Estado */}
                          <TableCell align='center'>
                            {renderStatusBadge(item.status)}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            <TablePagination
              component='div'
              count={getTotalCountForFilter(selectedFilter)}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={translate('ra.field.rows_per_page')}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />


          </Paper>
        </Box>
      </CardCommon>




      {/* Generic Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null })}
            color='primary'
            variant='outlined'
            style={{ textTransform: 'none' }}
          >
            {translate('ra.action.cancel')}
          </Button>
          <Button onClick={confirmDialog.onConfirm} color='primary' autoFocus variant='contained' style={{ textTransform: 'none' }}>
            {translate('ra.action.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal 1: Ajustar Costo / Venta (After Sales) */}
      <Dialog
        open={afterSalesModal.open}
        onClose={() => setAfterSalesModal({ open: false, item: null, destination: 'SAME_RESERVATION', adjustmentType: 'COST_ONLY' })}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>{translate('ra.action.adjust_cost_sale')}</DialogTitle>
        <DialogContent>
          <Box display='flex' flexDirection='column' gap={3} pt={1}>
            {/* Destino de Postventa */}
            <FormControl component='fieldset'>
              <FormLabel component='legend' sx={{ fontWeight: 'bold', mb: 1 }}>
                {translate('ra.field.aftersales_destination')}
              </FormLabel>
              <RadioGroup
                value={afterSalesModal.destination}
                onChange={(e) => setAfterSalesModal({ ...afterSalesModal, destination: e.target.value })}
              >
                <FormControlLabel
                  value='SAME_RESERVATION'
                  control={<Radio size='small' />}
                  label={translate('ra.field.same_reservation')}
                />
                <FormControlLabel
                  value='NEW_RESERVATION'
                  control={<Radio size='small' />}
                  label={translate('ra.field.new_reservation')}
                />
              </RadioGroup>
            </FormControl>

            {/* Tipo de Ajuste */}
            <FormControl fullWidth size='small'>
              <FormLabel sx={{ fontWeight: 'bold', mb: 1 }}>
                {translate('ra.field.adjustment_type')}
              </FormLabel>
              <Select
                value={afterSalesModal.adjustmentType}
                onChange={(e) => setAfterSalesModal({ ...afterSalesModal, adjustmentType: e.target.value })}
              >
                <MenuItem value='COST_ONLY'>{translate('ra.field.cost_only')}</MenuItem>
                <MenuItem value='COST_AND_SALE'>{translate('ra.field.cost_and_sale')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAfterSalesModal({ open: false, item: null, destination: 'SAME_RESERVATION', adjustmentType: 'COST_ONLY' })}
            color='primary'
            variant='outlined'
            disabled={actionLoading === afterSalesModal.item?.id}
            style={{ textTransform: 'none' }}
          >
            {translate('ra.action.cancel')}
          </Button>
          <Button
            onClick={handleAfterSalesSubmit}
            color='primary'
            variant='contained'
            disabled={actionLoading === afterSalesModal.item?.id}
            style={{ textTransform: 'none' }}
          >
            {actionLoading === afterSalesModal.item?.id ? <CircularProgress size={20} color='inherit' /> : translate('ra.action.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal 2: Reintentar Conciliación Bulk */}
      <Dialog
        open={retryModal.open}
        onClose={() => setRetryModal({ open: false, targetIds: [] })}
      >
        <DialogTitle>{translate('ra.field.confirm_retry_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {retryModal.targetIds.length === selectedItemIds.length && selectedItemIds.length > 0
              ? translate('ra.field.confirm_retry_msg', { count: selectedItemIds.length })
              : translate('ra.field.confirm_retry_all_msg', { count: retryModal.targetIds.length })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRetryModal({ open: false, targetIds: [] })}
            color='primary'
            variant='outlined'
            disabled={loading}
            style={{ textTransform: 'none' }}
          >
            {translate('ra.action.cancel')}
          </Button>
          <Button
            onClick={handleRetrySubmit}
            color='primary'
            autoFocus
            variant='contained'
            disabled={loading}
            style={{ textTransform: 'none' }}
          >
            {loading ? <CircularProgress size={20} color='inherit' /> : translate('ra.action.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal 3: Generar Orden de Pago */}
      <Dialog
        open={opModal.open}
        onClose={() => !opModal.submitting && setOpModal((prev) => ({ ...prev, open: false }))}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {translate('ra.action.generate_op') || 'Generar Orden de Pago'}
        </DialogTitle>
        <DialogContent dividers>
          <Box display='flex' flexDirection='column' gap={2.5} pt={1}>
            <TextField
              label={translate('ra.field.observations') || 'Observaciones'}
              fullWidth
              multiline
              rows={3}
              value={opModal.observations}
              onChange={(e) => setOpModal((prev) => ({ ...prev, observations: e.target.value }))}
              disabled={opModal.submitting}
            />
            <TextField
              label={translate('ra.field.due_date') || 'Vencimiento del Pago'}
              type='date'
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              value={opModal.dueDate}
              onChange={(e) => setOpModal((prev) => ({ ...prev, dueDate: e.target.value }))}
              disabled={opModal.submitting}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpModal((prev) => ({ ...prev, open: false }))}
            disabled={opModal.submitting}
            variant='outlined'
            style={{ textTransform: 'none' }}
          >
            {translate('ra.action.cancel') || 'Cancelar'}
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleGenerateOpSubmit}
            disabled={opModal.submitting}
            startIcon={opModal.submitting ? <CircularProgress size={20} color='inherit' /> : null}
            style={{ textTransform: 'none' }}
          >
            {translate('ra.action.generate') || 'Generar'}
          </Button>
        </DialogActions>
      </Dialog>

      <GenerateInvoiceModal
        open={invoiceModal.open}
        onClose={() => setInvoiceModal((prev) => ({ ...prev, open: false }))}
        onGenerate={handleGenerateInvoiceSubmit}
        conciliation={conciliation}
        submitting={invoiceModal.submitting}
      />

      {/* Check Items Modal (Duplicate Invoices Warning) */}
      <Dialog
        open={checkInvoiceModal.open}
        onClose={() => setCheckInvoiceModal({ open: false, itemsToCheck: [], formData: null, resolvedItems: [], submitting: false })}
        fullWidth
      >
        <DialogTitle>{translate('ra.title.invoice_already_exists') || 'El item que ingresaste ya existe en una factura'}</DialogTitle>
        <DialogContent>
          <CheckItemsModal items={checkInvoiceModal.itemsToCheck} />
        </DialogContent>
        <DialogActions style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', padding: '16px' }}>
          <Button
            color='error'
            onClick={() => setCheckInvoiceModal({ open: false, itemsToCheck: [], formData: null, resolvedItems: [], submitting: false })}
            variant='outlined'
            disabled={checkInvoiceModal.submitting}
          >
            {translate('ra.action.cancel') || 'Cancelar'}
          </Button>
          <LoadingButton
            loading={checkInvoiceModal.submitting}
            loadingPosition='start'
            variant='contained'
            onClick={() => executeCreateInvoice(checkInvoiceModal.formData, checkInvoiceModal.resolvedItems)}
            disabled={checkInvoiceModal.submitting}
          >
            {translate('ra.action.save') || 'Guardar'}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Change Conciliation Status Dialog */}
      <ChangeConciliationStatusDialog
        open={changeStatusDialogOpen}
        setOpen={setChangeStatusDialogOpen}
        currentStatus={generalStatus}
        conciliationId={id}
        onSuccess={loadData}
      />

    </Container>
  )
}
